// Loads the Razorpay Checkout.js script on demand (not in index.html, so
// it doesn't block initial page load / Lighthouse score for pages that
// never reach the payment step) and exposes a typed launcher.

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => { open: () => void };
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  theme?: { color?: string };
  handler: (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => void;
  modal?: { ondismiss?: () => void };
}

let scriptLoadPromise: Promise<void> | null = null;

function loadRazorpayScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout script.'));
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
}

export interface LaunchCheckoutParams {
  keyId: string;
  amount: number;
  currency: string;
  razorpayOrderId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  onSuccess: (response: { paymentId: string; orderId: string; signature: string }) => void;
  onDismiss?: () => void;
}

export async function launchRazorpayCheckout(params: LaunchCheckoutParams): Promise<void> {
  await loadRazorpayScript();

  const razorpay = new window.Razorpay({
    key: params.keyId,
    amount: params.amount,
    currency: params.currency,
    order_id: params.razorpayOrderId,
    name: 'Bharat Consultancy',
    description: 'Tax & Accounting Service Payment',
    prefill: {
      name: params.customerName,
      email: params.customerEmail,
      contact: params.customerPhone,
    },
    theme: { color: '#1B4D6B' }, // seal-blue, matches design tokens
    handler: (response) => {
      params.onSuccess({
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_order_id,
        signature: response.razorpay_signature,
      });
    },
    modal: {
      ondismiss: params.onDismiss,
    },
  });

  razorpay.open();
}
